import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { RiSurveyFill } from 'react-icons/ri';
import { FiCheckCircle } from 'react-icons/fi';
import _ from 'lodash';

import * as S from './styles';
import { Creators as CourseActions } from '../../../store/ducks/newCourse/actions';
import {
  useSurveyResponseStatus,
  isSurveyCompleted
} from '../../../hooks/useSurveyResponseStatus';

const SURVEY_LABEL = 'Pesquisa';
const ANSWERED_LABEL = 'Respondida';
const ACTIVATION_KEYS = ['Enter', ' '];
const UC_ITEM_CLASS = 'list-group-item list-group-item_sub2';
const COURSE_LEVEL_ICON_SIZE = 22;
const UC_LEVEL_ICON_SIZE = 20;

export const isCourseLevelSurvey = data => !_.get(data, 'logic.objectCode');

export const buildSurveyNavigation = (allObjects, data) => {
  const idx = allObjects.findIndex(
    obj => obj.id === data.id || obj.code === data.code || obj.cr_code === data.cr_code
  );
  return {
    prev: idx > 0 ? _.get(allObjects, idx - 1, null) : null,
    next: idx >= 0 && idx < allObjects.length - 1 ? _.get(allObjects, idx + 1, null) : null
  };
};

export const buildSelectedSurvey = data => ({
  ...data,
  type: 'SURVEY',
  code: data.cr_code,
  name: data.title || data.name,
  courseCode: data.courseCode || _.get(data, 'logic.courseCode', ''),
  rawSurvey: data
});

const FeedbackObject = ({ data, allObjects, closeCourseProgress }) => {
  const dispatch = useDispatch();
  const selected = useSelector(s => s.newCourse.selected);
  const { data: responseStatus } = useSurveyResponseStatus(data.cr_code);
  const isActive = selected.id === data.id;
  const isAvailable = data.available !== false;
  const courseLevel = isCourseLevelSurvey(data);

  useEffect(() => {
    if (isActive) {
      dispatch(CourseActions.setNavigationContent(buildSurveyNavigation(allObjects, data)));
    }
  }, [isActive]);

  const openSurvey = () => {
    if (!isAvailable) return;
    dispatch(CourseActions.setCourseSelected(buildSelectedSurvey(data)));
    if (closeCourseProgress) closeCourseProgress();
  };

  const handleKeyDown = event => {
    if (!ACTIVATION_KEYS.includes(event.key)) return;
    event.preventDefault();
    openSurvey();
  };

  return (
    <S.Container isActive={isActive}>
      <S.ContentItem className={courseLevel ? undefined : UC_ITEM_CLASS} courseLevel={courseLevel}>
        <S.ContentItemChild
          role="button"
          tabIndex={0}
          available={isAvailable}
          courseLevel={courseLevel}
          onClick={openSurvey}
          onKeyDown={handleKeyDown}
          title={data.availabilityDescription}
        >
          <S.ContainerIcon courseLevel={courseLevel}>
            <RiSurveyFill
              size={courseLevel ? COURSE_LEVEL_ICON_SIZE : UC_LEVEL_ICON_SIZE}
              role="img"
              aria-label={SURVEY_LABEL}
            />
          </S.ContainerIcon>
          <S.Info courseLevel={courseLevel}>
            <span className="info">{data.name}</span>
            {isSurveyCompleted(responseStatus) && (
              <S.Status>
                <FiCheckCircle size={14} aria-hidden="true" /> {ANSWERED_LABEL}
              </S.Status>
            )}
          </S.Info>
        </S.ContentItemChild>
      </S.ContentItem>
    </S.Container>
  );
};

FeedbackObject.propTypes = {
  data: PropTypes.shape({
    cr_code: PropTypes.string,
    name: PropTypes.string
  }).isRequired,
  allObjects: PropTypes.arrayOf(PropTypes.shape({})),
  closeCourseProgress: PropTypes.func
};

FeedbackObject.defaultProps = {
  allObjects: [],
  closeCourseProgress: null
};

export default FeedbackObject;
